import React, { useMemo } from "react";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type MRT_ColumnDef } from "material-react-table";

import { deleteCompanyLevel, deleteSiteLevel } from "api";

import { EsgDeleteIcon, EsgEditIcon } from "assets";

import {
    Body1SemiBold,
    DefaultTable,
    DeleteDialogIntensity,
    EMPTY_CELL_VALUE,
    useDialogControls,
} from "components";

import { useNotify } from "hooks";

import { AddIntensityModal } from "pages/authenticated/sitesv2/components/site-editor-form/components/AddIntensityModal";

import { getUTCDateFormat, isNullOrUndefined } from "utils";
import { CustomAccordionSummary, Accordion, AccordionDetails } from "./styles";
import { IIntensityData, IIntesityAccordionTable } from "./types";

const IntensityAccordionTable = ({
    intensityData,
    handleDeactivate,
    isCompanyLevel
}: IIntesityAccordionTable) => {
    const [expanded, setExpanded] = React.useState<string | false>("yearly");
    const deleteDialogControls = useDialogControls();
    const [deleteSelected, setDeleteSelected] = React.useState<any | undefined>();
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const { mutateAsync: deleteRequest } = useMutation(isCompanyLevel ? deleteCompanyLevel : deleteSiteLevel);
    const [selectedData, setSelectedData] = React.useState<any | undefined>();
    const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
    const notify = useNotify();
    const monthlyData: IIntensityData[] = intensityData?.monthly;
    const yearlyData: IIntensityData[] = intensityData?.yearly;

    const baseColumns = useMemo<MRT_ColumnDef<IIntensityData>[]>(
        () => [
            {
                accessorKey: "startDate",
                header: "Start Date",
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    if (isNullOrUndefined(value) || value === 0) {
                        return EMPTY_CELL_VALUE;
                    }
                    return dayjs(value as string)
                        .utc()
                        .format(getUTCDateFormat());
                },
            },
            {
                accessorKey: "endDate",
                header: "End Date",
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    if (isNullOrUndefined(value) || value === 0) {
                        return EMPTY_CELL_VALUE;
                    }
                    return dayjs(value as string)
                        .utc()
                        .format(getUTCDateFormat());
                },
            },
            {
                accessorKey: "intensityCategory",
                header: "Intensity Category",
                size: 140,
            },
            {
                accessorKey: "value",
                header: "Value",
                size: 140,
            },
            {
                accessorKey: "unit",
                header: "Unit",
                size: 140,
            },
            {
                accessorKey: "uploadDate",
                header: "Update Date",
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    if (isNullOrUndefined(value) || value === 0) {
                        return EMPTY_CELL_VALUE;
                    }
                    return dayjs(value as string)
                        .utc()
                        .format(getUTCDateFormat());
                },
            },
        ],
        [],
    );

    const columns = isCompanyLevel ? [...baseColumns] : [{
        accessorKey: "siteName",
        header: "Site Name",
        size: 150,
    }, ...baseColumns]

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const handleEditSelected = (rowData: any) => {
        setIsEditModalOpen(true);
        setSelectedData(rowData);
    };

    const handleCloseModal = () => {
        if (isAddModalOpen) {
            setIsAddModalOpen(false);
        }
        if (isEditModalOpen) {
            setIsEditModalOpen(false);
        }
        setSelectedData(undefined);
        handleDeactivate(true);
        setTimeout(() => {
            handleDeactivate(false);
        }, 2000);
    };

    const handleDeleteSelected = (id: number) => {
        if (id) {
            deleteRequest(id, {
                onError: () => {
                    deleteDialogControls.close();
                    notify.error(`Some error has happen while deleting the data!`);
                },
                onSuccess: () => {
                    deleteDialogControls.close();
                    notify.success(`Data successfully deleted!`);
                    handleDeactivate(true);
                    setTimeout(() => {
                        handleDeactivate(false);
                    }, 2000);
                },
            });
        }
    };

    const contentData = [
        {
            panel: "yearly",
            title: "Fiscal Year",
            data: yearlyData?.length > 0 ? yearlyData : []
        },
        {
            panel: "monthly",
            title: "Monthly",
            data: monthlyData?.length > 0 ? monthlyData : []
        }
    ]

    return (
        <>
            <div>
                {contentData.map((obj) => <Accordion
                    expanded={expanded === obj.panel}
                    onChange={handleChange(obj.panel)}
                    sx={{ marginBottom: 2, background: "white" }}
                >
                    <CustomAccordionSummary
                        aria-controls={`${obj.panel}-content`}
                        id={`${obj.panel}-header`}
                    >
                        <ArrowForwardIosSharpIcon
                            style={{
                                height: "14px",
                                width: "14px",
                                marginRight: "14px",
                                transform:
                                    expanded === obj.panel ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                        />
                        <Body1SemiBold>{obj.title}</Body1SemiBold>
                    </CustomAccordionSummary>
                    <AccordionDetails>
                        <DefaultTable
                            data={obj.data}
                            columns={columns}
                            enableRowActions
                            renderRowActions={(row) => (
                                <>
                                    {row?.row?.original.enabled ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "nowrap",
                                                gap: "0.5",
                                            }}
                                        >
                                            <Tooltip arrow placement="top" title="Edit">
                                                <IconButton
                                                    sx={{ width: "35px", height: "35px" }}
                                                    onClick={() => {
                                                        handleEditSelected(row?.row);
                                                    }}
                                                >
                                                    <EsgEditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip arrow placement="top" title="De-activated">
                                                <IconButton
                                                    sx={{ width: "35px", height: "35px" }}
                                                    onClick={() => {
                                                        deleteDialogControls.open();
                                                        setDeleteSelected(row?.row?.original?.id);
                                                    }}
                                                >
                                                    <EsgDeleteIcon color="red" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ) : (
                                        <span style={{ color: "red" }}>De-activated</span>
                                    )}
                                </>
                            )}
                        />
                    </AccordionDetails>
                </Accordion>)}
            </div>
            <AddIntensityModal
                isOpen={isAddModalOpen || isEditModalOpen}
                onClose={handleCloseModal}
                onCancel={handleCloseModal}
                editSite={selectedData}
                Intensity={isCompanyLevel ? "Company Level" : "Site Level"}
            />
            <DeleteDialogIntensity
                isOpen={deleteDialogControls.isOpen}
                onCancel={deleteDialogControls.close}
                onDeleteClick={() => handleDeleteSelected(deleteSelected)}
                title="De-activating an Intensity Metric?"
            />
        </>
    );
};

export default IntensityAccordionTable;